<ng-container *ngIf="vm$ | async as vm">
	<div class="flex border rounded-2xl overflow-hidden mb-10">
		<!-- LEFT SECTION -->
		<div
			class="w-80 shrink-0 p-4 border-r border-gray-200"
			data-intercom-target="writ-search-parties-list"
		>
			<div
				class="flex justify-between border-b items-center border-grey-lighter pb-5 mb-5"
			>
				<span class="text-lg font-semibold">Parties</span>
				<ui-button
					(click)="openSearchDialog(null)"
					label="Add"
					size="sm"
					icon="plus"
					data-intercom-target="writ-search-add-party-button"
					[testHook]="testHooks.AddButton"
					[disabled]="loading_()"
				/>
			</div>

			<!-- EMPTY STATE -->
			<div
				*ngIf="!vm?.writs || (vm?.writs | keyvalue).length === 0"
				class="rounded-lg bg-gray-50 flex items-center justify-center"
			>
				<div class="flex flex-col gap-4 text-center items-center p-6">
					<i-tabler name="user-off" class="text-gray-400 text-3xl"></i-tabler>
					<p class="text-gray-500">
						No parties have been added to the writ search.
					</p>
				</div>
			</div>

			<div
				*ngFor="let writ of vm.writs | keyvalue"
				class="flex mb-3 p-2 justify-between hover:bg-gray-100 cursor-pointer rounded-md items-center"
				[ngClass]="{ 'bg-gray-100': writ.key === vm.currentWritId }"
				(click)="onClickWrit(writ.key)"
				[testHook]="testHooks.ClickWritDiv"
			>
				<div class="flex gap-4">
					<div class="w-6">
						<app-checkbox
							click-stop-propagation
							[propagate]="false"
							(ngModelChange)="toggleSelectWrit(writ.key)"
							[ngModel]="writ?.value.selected"
							type="clear"
							[testHook]="testHooks.ToggleSelectWritCheckbox"
							[disabled]="loading_()"
						></app-checkbox>
					</div>
					<div class="cursor-pointer flex flex-col">
						<div>{{ writ.value.name }}</div>
						<p class="leading-none">
							<ng-container *ngFor="let type of writ.value.clientTypes">
								<span
									*ngIf="type !== MATTER_CLIENT_TYPE.OFFEROR"
									class="pr-1 text-xs text-gray-600"
									>{{ type | capitalize }}</span
								>
							</ng-container>
						</p>
					</div>
				</div>
				<div class="flex gap-4 items-center">
					<div>
						<div
							class="flex justify-center items-center text-sm w-7 h-7 bg-gray-200 rounded-md"
							[matTooltip]="
								'Total searches: ' + (writ.value.writSearches | keyvaluecount)
							"
						>
							{{ writ.value.searchesCount }}
						</div>
					</div>
					<div
						class="w-6 flex justify-center"
						*ngIf="
							writ.value?.writSearches | keyValuesOriginalOrder as writSearches
						"
					>
						<ng-container
							[ngSwitch]="writSearches[0]?.value?.certificate?.type"
						>
							<div
								*ngSwitchCase="'C'"
								class="text-green text-lg w-7 h-7 rounded-md bg-green-lighter flex items-center justify-center"
								matTooltip="Clear Certificate"
							>
								C
							</div>
							<div
								*ngSwitchCase="'E'"
								class="text-red text-lg w-7 h-7 rounded-md bg-red-lighter flex items-center justify-center"
								matTooltip="Execution Certificate"
							>
								E
							</div>
							<div
								*ngSwitchCase="'A'"
								class="text-amber-600 text-lg w-7 h-7 rounded-md bg-amber-100 flex items-center justify-center"
								matTooltip="Additional Information"
							>
								A
							</div>
						</ng-container>
					</div>
				</div>
			</div>
			<div class="flex justify-center mt-5">
				<ui-button
					[disabled]="vm.selectedWritsCount === 0 || loading_()"
					[fullWidth]="true"
					(click)="searchSelectedGroupedWrits()"
					[label]="'Purchase writ search (' + vm.selectedWritsCount + ')'"
					teranetLoginGuard
					[testHook]="testHooks.PurchaseButton"
					[loading]="loading_()"
				/>
			</div>
		</div>

		<!-- RIGHT SECTION -->
		<div class="flex-grow min-w-0">
			<div *ngIf="(vm.writs | keyvalue).length === 0" class="p-6 h-full">
				<div
					class="flex flex-col gap-4 text-center items-center p-6 w-full h-full rounded-lg bg-gray-50 justify-center"
				>
					<i-tabler name="user-off" class="text-gray-400 text-3xl"></i-tabler>
					<p class="text-gray-500">
						No writ search results as no parties<br />have been added to the
						writ search.
					</p>
				</div>
			</div>

			<ng-container *ngIf="vm.writs[vm.currentWritId] as writ">
				<div class="flex flex-col h-full overflow-auto">
					<div class="tab-wrapper flex items-center">
						<div class="shadow px-6 w-full">
							<div class="pt-6 pb-5 text-lg border-b border-grey-lighter">
								<span class="font-semibold">Results:</span> {{ writ.name }}
							</div>
							<div
								class="flex pt-4"
								data-intercom-target="writ-search-certificates-tab"
							>
								<h4
									class="h4 font-semibold !mb-0 pr-4 min-h-[38px] whitespace-nowrap shrink-0"
								>
									Certificate Date:
								</h4>
								<div class="flex w-full overflow-x-scroll no-scrollbar">
									<div
										*ngFor="
											let search of writ.writSearches | keyValuesOriginalOrder;
											trackBy: trackByWritSearch
										"
										(click)="onClickWritSearch(search.key)"
										class="px-3 py-1.5 rounded-t-md cursor-pointer border-b-2 border-gray-200 hover:text-green hover:border-b-green duration-300 text-ellipsis truncate min-w-12 shrink-0 flex gap-2 items-center"
										[ngClass]="{
											'text-green-dark border-green bg-green-lighter':
												vm.currentSearchId === search.key,
											'border-gray-300 text-grey-700':
												vm.currentSearchId !== search.key,
										}"
										[testHook]="testHooks.ClickWritSearchDiv"
									>
										<ng-container
											*ngIf="search.value.certificate; else tabPending"
										>
											<span *ngIf="search.value.certificate as certificate">{{
												(certificate.request_date | date) || certificate.number
											}}</span>
										</ng-container>
										<ng-template #tabPending>
											@if (loading_()) {
												<i-tabler
													name="loader"
													class="animate-spin text-gray-400"
												></i-tabler>
												<span class="text-xs">Searching...</span>
											} @else {
												<i-tabler name="x" class="text-red"></i-tabler>
												<span class="text-xs">Failed</span>
											}
										</ng-template>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- EMPTY STATE -->
					@if (loading_()) {
						<div
							class="h-full rounded-lg bg-gray-50 flex flex-col items-center justify-center p-8"
						>
							<div>
								<img
									src="/assets/images/house-searching.svg"
									alt="House Searching"
									class="w-[89px] h-[44px]"
								/>
							</div>
							<div class="mt-4 mb-6">
								<h6 class="text-gray-900 font-medium text-sm">
									Searching Teranet Registry
								</h6>
							</div>
							<div class="w-[70%] mb-6">
								<ui-progress-bar [animation]="true" />
							</div>
							<div class="text-gray-500 text-xs">
								Keep this page open until the search this complete... it's still
								faster than waiting at the registry counter.
							</div>
						</div>
					} @else {
						<div
							*ngIf="!writ.writSearches[vm.currentSearchId]"
							class="h-full rounded-lg bg-gray-50 flex items-center justify-center m-6"
						>
							<div class="flex flex-col gap-4 text-center items-center py-6">
								<i-tabler
									name="user-off"
									class="text-gray-400 text-3xl"
								></i-tabler>
								<p class="text-gray-500">
									No writ searches have been completed on this party.
								</p>
							</div>
						</div>
					}

					<div *ngIf="vm.currentSearchId">
						<div
							*ngIf="writ.writSearches[vm.currentSearchId] as search"
							class="m-6"
						>
							<div class="flex gap-4 my-6 flex-start">
								<div class="flex gap-6 w-64 shrink-0 items-start">
									<div
										*ngIf="search?.certificate"
										(click)="
											showSelectedCertificate(search.certificate, writ.name)
										"
										class="flex px-4 py-2 border items-center rounded-md gap-3 hover:bg-grey-lightest cursor-pointer"
										[testHook]="testHooks.ShowSelectedCertificateDiv"
									>
										<div class="flex bg-grey-lightest rounded p-2">
											<i-tabler
												name="file-type-pdf"
												class="text-gray-800 text-2xl"
											></i-tabler>
										</div>
										<ng-container [ngSwitch]="search.certificate.type">
											<div
												*ngSwitchCase="'C'"
												class="flex items-center gap-3 text-green"
											>
												<div>Clear certificate</div>
											</div>
											<div
												*ngSwitchCase="'E'"
												class="flex items-center gap-1 text-red-darker"
											>
												<div>Executions certificate</div>
											</div>
											<div
												*ngSwitchCase="'A'"
												class="flex items-center gap-1 text-red-darker"
											>
												<div>Additional Information</div>
											</div>
										</ng-container>
									</div>
								</div>
								<app-execution-diff
									[selectedGroup]="writ"
									[selectedWritId]="search.id"
								></app-execution-diff>
							</div>
							<div class="mb-8">
								<ui-button
									*ngIf="search?.certificate?.type !== 'C'"
									size="sm"
									[disabled]="vm.selectedExecutions.length === 0"
									(click)="purchaseSelectedExecutions()"
									[label]="
										'Purchase Selected (' + vm.selectedExecutions.length + ')'
									"
									teranetLoginGuard
									[testHook]="testHooks.PurchaseSelectedButton"
								/>
							</div>

							<table
								*ngIf="search?.certificate?.type !== 'C'"
								class="app-table table-auto w-full border-none"
								data-intercom-target="writ-search-executions-table"
							>
								<thead class="z-50 top-0 sticky text-sm">
									<tr>
										<th>
											<input
												type="checkbox"
												class="cursor-pointer"
												[checked]="vm.selectedExecutions.length"
												(click)="toggleSelectAllExecutions()"
												[testHook]="testHooks.SelectAllCheckbox"
											/>
										</th>
										<th class="text-left">Name</th>
										<th class="text-left">Execution #</th>
										<th class="text-left">Certification #</th>
										<th class="text-left">Issued</th>
										<th class="text-left">Effective</th>
										<th class="text-left">Judgment</th>
										<th></th>
										<th></th>
									</tr>
								</thead>
								<tbody class="text-sm">
									<tr
										*ngFor="
											let execution of search?.executions | keyvalue;
											trackBy: trackByExecution
										"
										[ngClass]="{
											'text-red bg-red-50':
												(execution?.value?.judgmentAmount | stringToNumber) >
												5000000,
										}"
									>
										<td class="flex justify-center items-center">
											<i-tabler
												name="exclamation-mark"
												[matTooltip]="execution.error"
												class="absolute -ml-6 text-red-darker w-4 h-4"
												*ngIf="execution?.value?.purchaseError"
											></i-tabler>
											<input
												*ngIf="execution?.value?.allowPurchase"
												class="cursor-pointer my-2"
												(click)="
													toggleSelectExecution(
														execution?.value?.execution_number
													)
												"
												type="checkbox"
												[checked]="
													vm.selectedExecutions.includes(execution.key)
												"
												[testHook]="testHooks.Input1"
											/>
											<i-tabler
												name="file-type-pdf"
												class="text-red-darker text-2xl"
												*ngIf="execution?.value?.purchased"
												(click)="
													showSelectedExecution(
														execution?.value?.execution.certificate,
														execution?.value?.execution_number
													)
												"
												class="cursor-pointer"
											></i-tabler>
											<i-tabler
												name="loader"
												class="animate-spin text-red-darker text-2xl"
												*ngIf="execution?.value?.purchasing"
												[testHook]="testHooks.WritPurchaseInProgressIcon"
											></i-tabler>
										</td>
										<td>
											{{ execution?.value?.first_name | capitalize }}
											{{ execution?.value?.last_name | capitalize }}
											{{ execution?.error }}
										</td>
										<td>
											{{ execution?.value?.execution_number }}
										</td>
										<td>
											{{ execution?.value?.execution?.certificate?.number }}
										</td>
										<td>
											{{ execution?.value?.execution?.summary?.Issuedate }}
										</td>
										<td>
											{{ execution?.value?.execution?.summary?.Effectivedate }}
										</td>
										<td>
											<div
												*ngIf="execution?.value?.judgment"
												[matTooltip]="
													(execution?.value?.judgmentAmount | stringToNumber) >
													5000000
														? 'Note: Solicitor affidavit required'
														: ''
												"
											>
												<span>
													<i-tabler
														name="exclamation-mark"
														*ngIf="
															(execution?.value?.judgmentAmount
																| stringToNumber) > 5000000
														"
														class="text-xl mr-2"
													></i-tabler>
													{{
														execution?.value?.judgmentAmount | centsToDollars
													}}
												</span>
											</div>
										</td>
										<td>
											<ui-button
												*ngIf="execution.value?.showAddBillAndDebt"
												size="sm"
												(click)="addBillAndDebt(execution?.value)"
												icon="plus"
												label="Debt"
												[testHook]="testHooks.DebtButton"
											/>
											<ui-button
												*ngIf="execution.value?.showLinkedBillAndDebt"
												size="sm"
												(click)="openBillAndDebt(execution?.value?.debtId)"
												label="View Debt"
												[testHook]="testHooks.ViewDebtButton"
											/>
										</td>
										<td>
											<ui-button
												*ngIf="execution.value?.showAddRequisition"
												size="sm"
												(click)="addRequisition(execution?.value)"
												icon="plus"
												label="Req."
												[testHook]="testHooks.ReqButton"
											/>
											<ui-button
												*ngIf="execution.value?.showLinkedRequisition"
												size="sm"
												(click)="
													openRequisition(execution?.value?.requisitionId)
												"
												label="View Req."
												[testHook]="testHooks.ViewReqButton"
											/>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</ng-container>
		</div>
	</div>
</ng-container>
